/**
 * NumberingMaskEditor — F1 (Globalne typy dokumentów, zobacz notatka planu
 * `glimmering-bouncing-hamster.md` §„Implementacja — frontend").
 *
 * Kontrolowany, prezentacyjny komponent wyekstrahowany z `DocumentTypeFormModal`
 * (blok maski numeracji + pole bazowe daty + polityka resetu + live preview).
 * Zero własnego stanu — wszystkie wartości i handlery przychodzą z rodzica, żeby
 * dało się go współdzielić z przyszłym modalem typu globalnego (panel ITE).
 *
 * Logika podglądu/walidacji maski pozostaje w `lib/document-number-preview.ts`
 * (pure helpers, niezmienione).
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { NumberingBaseFieldOption } from '@/components/documents/useNumberingBaseFieldOptions';
import { Dropdown, type DropdownOption } from '@/components/Dropdown';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { previewDocumentNumber } from '@/lib/document-number-preview';

/** Domyślny prefiks `data-testid` — zgodny z dotychczasowymi ID w `DocumentTypeFormModal`. */
const DEFAULT_TEST_ID_PREFIX = 'document-type-form';

export interface NumberingMaskEditorProps {
  /** Kod typu dokumentu — używany tylko do podpowiedzi w placeholderze maski. */
  code: string;
  numberingFormat: string;
  numberingBaseField: string;
  /**
   * Opcje pola daty numeracji (core + ext) budowane przez rodzica
   * (`useNumberingBaseFieldOptions`). Scope tenanta = core + ext; panel ITE = tylko core.
   * WYMAGANE — wszyscy wołający budują je przez `useNumberingBaseFieldOptions` (żaden nie
   * potrzebuje już fallbacku do samego pola domyślnego).
   */
  baseFieldOptions: NumberingBaseFieldOption[];
  numberingReset: 'continuous' | 'yearly' | 'monthly';
  onFormatChange: (value: string) => void;
  onBaseFieldChange: (value: string) => void;
  onResetChange: (value: 'continuous' | 'yearly' | 'monthly') => void;
  /** Blokuje wszystkie pola (np. zapis w toku). */
  disabled?: boolean;
  /** Prefiks `data-testid` renderowanych elementów — domyślnie `document-type-form`. */
  testIdPrefix?: string;
  /**
   * Komunikat błędu maski wyliczony przez rodzica (np. w `validate()` przy submit).
   * Komponent NIE liczy własnego live-błędu — zachowanie identyczne z oryginalnym
   * blokiem w `DocumentTypeFormModal` (błąd pojawia się dopiero po próbie zapisu).
   */
  formatError?: string;
}

export function NumberingMaskEditor({
  code,
  numberingFormat,
  numberingBaseField,
  baseFieldOptions,
  numberingReset,
  onFormatChange,
  onBaseFieldChange,
  onResetChange,
  disabled,
  testIdPrefix = DEFAULT_TEST_ID_PREFIX,
  formatError,
}: NumberingMaskEditorProps) {
  const { t } = useTranslation();

  // Wartość zapisana w typie, ale NIEOBECNA w opcjach (np. pole ext skasowane po tym, jak
  // zostało wybrane jako baza numeracji) — bez tego Dropdown pokazywałby placeholder/pustkę,
  // a user nie widziałby, że baza wskazuje na usunięte pole. Dokładamy syntetyczną,
  // WYŁĄCZONĄ opcję z etykietą sluga + markerem „(nieaktywne)", żeby stan był widoczny;
  // wybór innego pola nadal wymaga aktywnej opcji z listy.
  const dropdownOptions = useMemo<DropdownOption[]>(() => {
    if (numberingBaseField === '' || baseFieldOptions.some((o) => o.value === numberingBaseField)) {
      return baseFieldOptions;
    }
    return [
      ...baseFieldOptions,
      {
        value: numberingBaseField,
        label: t('tenant.documentTypes.form.numberingBaseFieldInactiveOption', {
          slug: numberingBaseField,
        }),
        disabled: true,
      },
    ];
  }, [baseFieldOptions, numberingBaseField, t]);

  return (
    <>
      <FormField label={t('tenant.documentTypes.form.numberingFormat')} error={formatError}>
        <Input
          value={numberingFormat}
          onChange={(e) => onFormatChange(e.target.value)}
          disabled={disabled}
          placeholder={
            code.trim()
              ? t('tenant.documentTypes.form.numberingFormatPlaceholderWithCode', {
                  code: code.trim(),
                })
              : t('tenant.documentTypes.form.numberingFormatPlaceholderFallback')
          }
          data-testid={`${testIdPrefix}-numbering-format`}
        />
      </FormField>
      <FormField label={t('tenant.documentTypes.form.numberingBaseField')}>
        <Dropdown
          value={numberingBaseField}
          onChange={(v) => onBaseFieldChange(v)}
          disabled={disabled}
          data-testid={`${testIdPrefix}-numbering-base-field`}
          options={dropdownOptions}
        />
      </FormField>
      <FormField label={t('tenant.documentTypes.form.numberingReset')}>
        <Dropdown
          value={numberingReset}
          onChange={(v) => onResetChange(v as 'continuous' | 'yearly' | 'monthly')}
          disabled={disabled}
          data-testid={`${testIdPrefix}-numbering-reset`}
          options={[
            {
              value: 'continuous',
              label: t('tenant.documentTypes.form.numberingResetOptions.continuous'),
            },
            {
              value: 'yearly',
              label: t('tenant.documentTypes.form.numberingResetOptions.yearly'),
            },
            {
              value: 'monthly',
              label: t('tenant.documentTypes.form.numberingResetOptions.monthly'),
            },
          ]}
        />
      </FormField>
      <div className="rounded-md border border-border-primary bg-bg-secondary px-3 py-2">
        <p className="mb-1 text-xs text-text-tertiary">
          {t('tenant.documentTypes.form.numberingPreview')}
        </p>
        {!numberingFormat.trim() ? (
          <span
            className="text-sm text-text-muted"
            data-testid={`${testIdPrefix}-numbering-preview`}
          >
            {t('tenant.documentTypes.form.numberingPreviewEmpty')}
          </span>
        ) : previewDocumentNumber(numberingFormat) === null ? (
          <span
            className="text-sm text-text-muted"
            data-testid={`${testIdPrefix}-numbering-preview`}
          >
            {t('tenant.documentTypes.form.numberingPreviewInvalid')}
          </span>
        ) : (
          <>
            <span
              className="font-mono text-sm text-text-primary"
              data-testid={`${testIdPrefix}-numbering-preview`}
            >
              {previewDocumentNumber(numberingFormat)}
            </span>
            <p className="mt-1 text-xs text-text-muted">
              {t('tenant.documentTypes.form.numberingPreviewExampleHint')}
            </p>
          </>
        )}
      </div>
    </>
  );
}
